import each from 'lodash/each';
import get from 'lodash/get';
import includes from 'lodash/includes';

import { getState } from '../redux/state';
import config from '../config';
import logger from './logger';
// eslint-disable-next-line import/no-cycle
import { updateQueryStringParameter } from './fetch';

import {
  BLOCK_REFRESH_TIMEOUT,
  MAX_BLOCK_REFRESH_TIMEOUT,
  MAX_FETCH_RETRIES,
  REFRESH_TIMEOUT,
} from './constants/fetchClient';

let isRefreshing = false;
// Defaulted to the earliest epoch time to ensure first refresh.
let lastCompletedRefresh = new Date(null);

/**
 * Set timeout that can be used with async/await.
 *
 * @param {Number} timeout time to wait before resolving promise.
 * @return {Promise} promise that resolves when timeout is reached.
 */
async function asyncSetTimeout(timeout) {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
}

/**
 * Get the proper headers to add the requests.
 *
 * @param {Object} options Options passed into fetch call.
 */
function getRequestHeaders(options) {
  const state = getState();
  const cloudflareHeaders = get(state, 'application.cloudflareHeaders', {});
  const requestHeaders = {
    'Content-Type': 'application/json',
    'X-Arrival-Lock': get(state, 'application.arrivalLock'),
    'X-Arrival-Referer': get(state, 'application.browserReferer'),
    ...get(options, 'headers', {}),
  };
  // Add cloudflare headers
  each(cloudflareHeaders, (value, key) => {
    if (value) {
      if (key === 'cf-connecting-ip') {
        requestHeaders['jc-connecting-ip'] = value;
      } else {
        requestHeaders[key] = value;
      }
    }
  });

  return requestHeaders;
}

/**
 * Make a request and throw an error if response code is not in successful range.
 *
 * @param {String} url                      The url of the request.
 * @param {Object} options
 * @param {String} options.method           The method for the request.
 * @param {Object} options.headers          The headers to send with the request.
 * @param {Object} options.body             The body to send with the request.
 */
async function makeRequest(url, options) {
  let requestURL = url;
  // Skip preflight for reportable requests
  const isReportableRequest = includes(url, '/v2/report');
  // Optional timeout for request
  let abortController;
  if (options.timeout) {
    abortController = new AbortController();
    setTimeout(() => {
      abortController.abort();
    }, options.timeout);
  }

  const requestOptions = {
    method: options.method || 'GET',
    credentials: 'include',
    headers: isReportableRequest ? {} : getRequestHeaders(options),
    body: options.body ? JSON.stringify(options.body) : null,
    signal: get(abortController, 'signal'),
    referrerPolicy: 'no-referrer-when-downgrade',
  };

  // If form data is supplied, do not stringify the body and
  // remove the Content-Type header so that the browser can set it itself.
  if (options.formData) {
    requestOptions.body = options.body;

    delete requestOptions.headers['Content-Type'];
  }

  // For GETs, avoid preflight cost by forming simple requests
  if (requestOptions.method === 'GET' || isReportableRequest) {
    delete requestOptions.headers['Content-Type'];
    delete requestOptions.headers['X-Arrival-Lock'];

    requestURL = updateQueryStringParameter(url, 'x-arrival-lock', get(getState(), 'application.arrivalLock'));
  }

  // We need to add an analytics key for reportable events as a query param because reportable events skip preflight.
  if (isReportableRequest) {
    requestURL = updateQueryStringParameter(url, 'x-analytics-key', config.analyticsKey);
  }

  const response = await fetch(requestURL, requestOptions);

  // Throw an error if the status code is not a successful status code.
  if (response.status < 200 || response.status > 299) {
    throw response;
  }

  // Otherwise, return the response.
  return response;
}

/**
 * Attempt to refresh a users tokens.
 *
 * @param {String} url              The url of the request.
 * @param {Object} options
 * @param {String} options.method   The method for the request.
 * @param {Object} options.headers  The headers to send with the request.
 * @param {Object} options.body     The body to send with the request.
 */
async function refreshTokens(url, options) {
  const response = await fetch(`${config.apiUrl}/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: getRequestHeaders(options),
    referrerPolicy: 'no-referrer-when-downgrade',
  });

  // Throw an error if the status code is not a successful status code.
  if (response.status < 200 || response.status > 299) {
    throw response;
  }

  // Otherwise, return the response.
  return response;
}

/**
 *
 * @param {String} url                   The url of the request.
 * @param {Object} options               Options to be passed to refresh.
 * @param {Number} blockRefreshWaitTime  Number of ms already spent blocking refresh.
 */
async function refreshTokensWithRetry(url, options, blockRefreshWaitTime = 0) {
  if (blockRefreshWaitTime < MAX_BLOCK_REFRESH_TIMEOUT) {
    // If the tokens are currently being refreshed, wait and try again.
    if (isRefreshing) {
      await asyncSetTimeout(BLOCK_REFRESH_TIMEOUT);

      return refreshTokensWithRetry(url, options, blockRefreshWaitTime + BLOCK_REFRESH_TIMEOUT);
    }

    // If the last refresh happened more than 60 seconds ago, attempt a refresh.
    if (new Date().getTime() - lastCompletedRefresh.getTime() > REFRESH_TIMEOUT) {
      isRefreshing = true;

      try {
        await refreshTokens(url, options);
      } catch (error) {
        lastCompletedRefresh = new Date();
        isRefreshing = false;

        // If the /v1/me endpoint throws an error on refresh, do not retry.
        if (url === `${config.apiUrl}/v1/me`) {
          throw error;
        }

        // Otherwise, swallow the error and allow client to retry as unauthenticated user.
        return false;
      }

      lastCompletedRefresh = new Date();
      isRefreshing = false;
    }

    return true;
  }

  logger.logDebug('Refresh with retry exceeded block refresh timeout', {
    url,
    method: options.method,
    headers: options.headers,
    body: options.body,
  });

  return false;
}

/**
 * Make a request with the ability to refresh tokens in the event of a 401.
 *
 * @param {String} url                      The url of the request.
 * @param {Object} options
 * @param {String} options.method           The method for the request.
 * @param {Object} options.headers          The headers to send with the request.
 * @param {Object} options.body             The body to send with the request.
 */
export default async function fetchWithRefresh(url, options = {}, retry = 0) {
  try {
    // Make request with current tokens.
    const response = await makeRequest(url, options);
    const fullResponse = !!options.fullResponse || options.method === 'DELETE';

    // Should just return if no-content response
    if (response.status === 204) {
      return true;
    }

    // Must return resolved value after await to properly handle try/catch.
    return fullResponse ? response : await response.json();
  } catch (error) {
    logger.logDebug('Received error from request', {
      url,
      status: error.status,
      statusText: error.statusText,
      method: options.method,
      headers: options.headers,
      body: options.body,
    });
    const disableUnauthRefresh = !!options.disableUnauthRefresh;

    // If there is authorization error, attempt to refresh the user's tokens.
    if (error.status === 401 && !disableUnauthRefresh) {
      // Attempt to refresh the tokens with retry incase a refresh is in progress.
      await refreshTokensWithRetry(url, options);

      if (retry < MAX_FETCH_RETRIES) {
        return fetchWithRefresh(url, options, retry + 1);
      }

      throw new Error(`Unable to make request: ${url}`);
    }

    // Otherwise, throw the error to be handled by the original caller.
    const errorObject = get(error, 'json') ? await error.json() : error;
    throw errorObject;
  }
}
